import { useRef, useState } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';
import { useInView } from 'react-intersection-observer';
import ReactPlayer from 'react-player';
import { GatsbyImage } from 'gatsby-plugin-image';

import { generateClassName } from '@klv/shared/utils/functions';
import { Wrapper } from '@klv/utils/wrapper';
import { VideoColorPlayButton } from '@klv/atoms/video-color-play-button';
import type { IVideoMediaProps } from './types';
import * as styles from './styles/video.module.scss';

const Video = ({
  backgroundColor,
  poster,
  preRollUrl,
  videoDuration,
  customPadding = { customPaddingBottom: 'none', customPaddingTop: 'none' },
  videoPlayButtonColor,
  videoOverlayTitle,
  videoTitle,
  videoUrl,
}: IVideoMediaProps) => {
  const { t } = useTranslation();

  const playButtonArea = useRef<HTMLButtonElement>(null);
  const playerRef = useRef<ReactPlayer>(null);

  const [ref] = useInView({ threshold: 0.25, triggerOnce: true });
  const [moreInViewRef, moreInView] = useInView({ threshold: 0.4, triggerOnce: true });

  const [showEmbed, setShowEmbed] = useState(false);

  const handleSwitch = () => {
    setShowEmbed(true);
    playButtonArea?.current?.blur();
  };

  const playerProps = {
    url: videoUrl,
    loop: false,
    controls: true,
    playing: showEmbed,
    width: '100%',
    height: '100%',
  };

  const preRollConfig = {
    vimeo: {
      playerOptions: {
        autopause: false,
        background: true,
        keyboard: false,
        loop: true,
        responsive: true,
        texttrack: t('langCodes_recaptchaLanguage') === 'en' ? '' : t('langCodes_recaptchaLanguage'),
      },
    },
  };

  const posterImage = poster?.gatsbyImage || poster?.localFile?.childImageSharp?.gatsbyImageData;

  return (
    <Wrapper customPadding={customPadding} backgroundColor={backgroundColor}>
      <div className={styles.videoModule} ref={ref}>
        <div className={styles.videoContent} ref={moreInViewRef}>
          {videoUrl && (
            <button
              className={generateClassName([styles.playButtonArea, showEmbed ? styles.showEmbed : ''])}
              aria-label={
                videoTitle
                  ? `${t('organisms_video_playVideo')}: ${videoTitle}`
                  : `${t('organisms_video_playVideo')}`
              }
              onClick={handleSwitch}
              ref={playButtonArea}
              tabIndex={showEmbed ? -1 : 0}
            >
              <VideoColorPlayButton videoPlayButtonColor={videoPlayButtonColor} />
              {videoDuration && <div className={styles.videoDuration}>{videoDuration}</div>}
              {videoOverlayTitle && (
                <div className={styles.videoOverlayTitleWrapper}>
                  <div className={styles.videoOverlayTitle}>{videoOverlayTitle}</div>
                </div>
              )}
            </button>
          )}

          <div className={generateClassName([styles.clipWrapper, showEmbed ? styles.animate : ''])}>
            {preRollUrl && posterImage && (
              <>
                <GatsbyImage alt="" image={posterImage} />
                <ReactPlayer
                  className={styles.preRollPlayer}
                  muted
                  volume={0}
                  playsinline
                  url={preRollUrl}
                  config={preRollConfig}
                  width="100%"
                  height="100%"
                />
              </>
            )}
            {!preRollUrl && posterImage && <GatsbyImage alt="" image={posterImage} />}
          </div>

          {videoUrl && (
            <div className={generateClassName([styles.embedWrapper, showEmbed ? styles.animate : ''])}>
              {moreInView && (
                <ReactPlayer
                  className={styles.reactPlayer}
                  ref={playerRef}
                  config={{
                    vimeo: {
                      playerOptions: {
                        playsinline: true,
                        texttrack: t('langCodes_recaptchaLanguage') === 'en' ? '' : t('langCodes_recaptchaLanguage'),
                      },
                    },
                  }}
                  {...playerProps}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </Wrapper>
  );
};

export default Video;
